import React, { useState } from 'react';

const App = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [user, setUser] = useState(null);
  const [registeredUser, setRegisteredUser] = useState(null);

  // Componente StreamingHeader
  const StreamingHeader = ({ onNavigate, user, handleLogin, handleLogout }) => {
    return (
      <header className="fixed top-0 left-0 right-0 bg-white shadow-lg z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl md:text-3xl font-extrabold text-blue-600 cursor-pointer" 
                onClick={() => onNavigate('home')}>
              MonosStreaming
            </h1>
          </div>
          <nav className="hidden md:flex items-center space-x-8">
            <button onClick={() => onNavigate('home')} 
                    className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
              Inicio
            </button>
            <button onClick={() => onNavigate('platforms')} 
                    className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
              Plataformas
            </button>
            <button onClick={() => onNavigate('about')} 
                    className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
              Acerca de
            </button>
            <button onClick={() => onNavigate('contact')} 
                    className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
              Contacto
            </button>
          </nav>
          <div className="flex items-center space-x-4">
            {user ? (
              <>
                <span className="text-blue-600 font-semibold">¡Hola, {user}!</span>
                <button onClick={handleLogout} 
                        className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium">
                  Cerrar Sesión
                </button>
              </>
            ) : (
              <>
                <button onClick={() => onNavigate('login')} 
                        className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium">
                  Iniciar Sesión
                </button>
                <button onClick={() => onNavigate('register')} 
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
                  Registrarse
                </button>
              </>
            )}
          </div>
        </div>
      </header>
    );
  };
  
  // Componente StreamingHero
  const StreamingHero = ({ onExplore }) => {
    return (
      <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 text-white py-20 md:py-32 px-4 md:px-8 lg:px-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 md:mb-8 leading-tight">
            Tu Portal de <span className="text-yellow-300">Entretenimiento</span> Digital
          </h1>
          <p className="text-lg md:text-2xl mb-8 md:mb-12 max-w-3xl mx-auto leading-relaxed text-gray-200">
            Descubre, compara y suscríbete a las mejores plataformas de streaming. 
            Todo el entretenimiento que necesitas en un solo lugar.
          </p>
          <button
            onClick={onExplore}
            className="px-8 py-4 md:px-12 md:py-5 bg-yellow-400 text-blue-900 rounded-full shadow-xl hover:bg-yellow-300 transition-all duration-300 transform hover:-translate-y-2 text-lg md:text-xl font-bold tracking-wide"
          >
            Explorar Plataformas
          </button>
        </div>
      </section>
    );
  };
  
  // Componente StreamingPlatformCard
  const StreamingPlatformCard = ({ platform }) => {
    const handleSubscribeClick = () => {
      const phoneNumber = '51954380327';
      const message = `¡Hola! Estoy interesado en suscribirme a ${platform.name}. ¿Podrías darme más información?`;
      const whatsappUrl = `whatsapp://send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
    };
  
    return (
      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-500 hover:scale-105 hover:shadow-3xl border border-gray-100 group">
        <div className="relative h-48 sm:h-56 overflow-hidden bg-gray-200 flex items-center justify-center">
          <img
            src={platform.image}
            alt={platform.name}
            className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110 p-4"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
          <span className="absolute top-4 right-4 bg-blue-600 text-white text-xs sm:text-sm font-bold px-3 py-1.5 rounded-full shadow-md uppercase tracking-wide">
            {platform.category}
          </span>
        </div>
        <div className="p-5 sm:p-7">
          <h3 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">{platform.name}</h3>
          <p className="text-gray-600 text-sm sm:text-base mb-4 sm:mb-5 line-clamp-3">{platform.description}</p>
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <span className="text-2xl sm:text-4xl font-extrabold text-gray-900 mb-2 sm:mb-0">
              S/ {platform.price}<span className="text-base sm:text-xl text-gray-500">/mes</span>
            </span>
            <button
              onClick={handleSubscribeClick} 
              className="px-5 py-2 sm:px-7 sm:py-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 transform hover:-translate-y-1.5 text-sm sm:text-lg font-semibold w-full sm:w-auto"
            >
              Suscribirse
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Componente StreamingPlatformGrid
  const StreamingPlatformGrid = ({ platforms }) => {
    return (
      <section className="py-16 md:py-20 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-gray-50 to-gray-100">
        <h2 className="text-3xl md:text-5xl font-extrabold text-center text-gray-900 mb-12 md:mb-16 leading-tight">
          Explora Nuestro Catálogo de Plataformas
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 md:gap-10 max-w-7xl mx-auto">
          {platforms.map((platform) => (
            <StreamingPlatformCard key={platform.id} platform={platform} />
          ))}
        </div>
      </section>
    );
  };
  
  // Componente Registro
  const Registro = ({ handleRegister }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
  
    const handleSubmit = () => {
      if (name && email && password) {
        handleRegister(name, email, password);
      } else {
        alert('Por favor, completa todos los campos');
      }
    };
  
    return (
      <section className="py-16 md:py-20 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 md:mb-8">Registro</h2>
          <div className="space-y-6 md:space-y-7 max-w-xl mx-auto">
            <div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre Completo"
                className="w-full px-5 py-3 md:px-6 md:py-3.5 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-3 focus:ring-blue-500 transition text-base md:text-lg placeholder-gray-500"
              />
            </div>
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Correo Electrónico"
                className="w-full px-5 py-3 md:px-6 md:py-3.5 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-3 focus:ring-blue-500 transition text-base md:text-lg placeholder-gray-500"
              />
            </div>
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Contraseña"
                className="w-full px-5 py-3 md:px-6 md:py-3.5 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-3 focus:ring-blue-500 transition text-base md:text-lg placeholder-gray-500"
              />
            </div>
            <button
              onClick={handleSubmit}
              className="w-full px-6 py-3 md:px-8 md:py-4 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 transform hover:-translate-y-1.5 text-lg md:text-xl font-bold tracking-wide"
            >
              Registrarse
            </button>
          </div>
        </div>
      </section>
    );
  };
  
  // Componente Login (Iniciar Sesión)
  const Login = ({ handleLogin, user }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
  
    const handleSubmit = () => {
      if (user && user.email && user.password) {
        if (email === user.email && password === user.password) {
          handleLogin(user.name);
        } else {
          alert('Credenciales incorrectas');
        }
      } else {
        alert('No hay usuario registrado. Por favor regístrate primero.');
      }
    };
  
    return (
      <section className="py-16 md:py-20 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 md:mb-8">Iniciar Sesión</h2>
          <div className="space-y-6 md:space-y-7 max-w-xl mx-auto">
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Correo Electrónico"
                className="w-full px-5 py-3 md:px-6 md:py-3.5 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-3 focus:ring-blue-500 transition text-base md:text-lg placeholder-gray-500"
              />
            </div>
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Contraseña"
                className="w-full px-5 py-3 md:px-6 md:py-3.5 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-3 focus:ring-blue-500 transition text-base md:text-lg placeholder-gray-500"
              />
            </div>
            <button
              onClick={handleSubmit}
              className="w-full px-6 py-3 md:px-8 md:py-4 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 transform hover:-translate-y-1.5 text-lg md:text-xl font-bold tracking-wide"
            >
              Iniciar Sesión
            </button>
          </div>
        </div>
      </section>
    );
  };
  
  // Componente AboutUs
  const AboutUs = () => {
    return (
      <section className="py-16 md:py-20 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 md:mb-8">Acerca de Nosotros</h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg md:text-xl text-gray-600 mb-6 leading-relaxed">
              MonosStreaming es tu portal definitivo para el entretenimiento digital. Nos especializamos en conectar 
              a los usuarios con las mejores plataformas de streaming disponibles en el mercado.
            </p>
            <p className="text-lg md:text-xl text-gray-600 mb-6 leading-relaxed">
              Nuestro objetivo es simplificar tu experiencia de entretenimiento, ofreciéndote información clara 
              y actualizada sobre precios, contenido y características de cada plataforma.
            </p>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Con sede en La Libertad, Guadalupe, Perú, estamos comprometidos a brindar el mejor servicio 
              y soporte a nuestros usuarios.
            </p>
            
            <h3 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-4 mt-8">
              Fundadores
            </h3>
            <p className="text-lg md:text-xl text-gray-700 mb-6 md:mb-8 leading-relaxed">
              • ALBARRAN JARA CARLOS FERNANDO <br />
              • MONTENGRO BACA ZEE RICARDO <br />
              • NORIEGA OLIVA CHRISTIAN ALFREDO <br />
              • PALACIOS HERNANDEZ GEORGE ELVIS <br />
              • RODRIGUEZ PRECIADO ANDRE JHONEL
            </p>
          </div>
        </div>
      </section>
    );
  };
  
  // Componente Contact
  const Contact = () => {
    return (
      <section className="py-16 md:py-20 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 md:mb-8">Contacto</h2>
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Información de Contacto</h3>
              <div className="space-y-4 text-left">
                <div className="flex items-center">
                  <span className="font-semibold text-gray-700 w-20">Email:</span>
                  <a href="mailto:info@monosstreaming.com" className="text-blue-600 hover:text-blue-800">
                    info@monosstreaming.com
                  </a>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold text-gray-700 w-20">Teléfono:</span>
                  <a href="tel:+51954380327" className="text-blue-600 hover:text-blue-800">
                    +51 954380327
                  </a>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold text-gray-700 w-20">Dirección:</span>
                  <span className="text-gray-600">La Libertad, Guadalupe, Perú</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                const phoneNumber = '51954380327';
                const message = '¡Hola! Me gustaría obtener más información sobre MonosStreaming.';
                const whatsappUrl = `whatsapp://send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`;
                window.open(whatsappUrl, '_blank');
              }}
              className="px-8 py-4 bg-green-500 text-white rounded-full shadow-lg hover:bg-green-600 transition-all duration-300 transform hover:-translate-y-1 text-lg font-semibold"
            >
              Contactar por WhatsApp
            </button>
          </div>
        </div>
      </section>
    );
  };
  
  // Componente StreamingFooter
  const StreamingFooter = () => {
    return (
      <footer className="bg-gray-900 text-gray-300 py-10 md:py-12 px-4 md:px-8 lg:px-16">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 border-b border-gray-700 pb-8 md:pb-10 mb-8 md:mb-10">
          <div>
            <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-4 md:mb-5">MonosStreaming</h3>
            <p className="text-gray-400 text-sm md:text-base leading-relaxed">
              Tu portal definitivo para el entretenimiento digital. Descubre, compara y suscríbete a las mejores plataformas de streaming.
            </p>
          </div>
          <div>
            <h4 className="text-lg md:text-xl font-semibold text-white mb-4 md:mb-5">Navegación Rápida</h4>
            <ul className="space-y-2 md:space-y-3">
              <li><a href="#" className="hover:text-blue-400 transition-colors text-sm md:text-base">Inicio</a></li>
              <li><a href="#" className="hover:text-blue-400 transition-colors text-sm md:text-base">Plataformas</a></li>
              <li><a href="#" className="hover:text-blue-400 transition-colors text-sm md:text-base">Preguntas Frecuentes</a></li>
              <li><a href="#" className="hover:text-blue-400 transition-colors text-sm md:text-base">Términos y Condiciones</a></li>
              <li><a href="#" className="hover:text-blue-400 transition-colors text-sm md:text-base">Política de Privacidad</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-lg md:text-xl font-semibold text-white mb-4 md:mb-5">Contáctanos</h4>
            <p className="text-gray-400 text-sm md:text-base leading-relaxed">
              Email: <a href="mailto:info@monosstreaming.com" className="hover:text-blue-400 transition-colors">info@monosstreaming.com</a> <br />
              Teléfono: <a href="tel:+51954380327" className="hover:text-blue-400 transition-colors">+51 954380327</a> <br />
              Dirección: La Libertad, Guadalupe, Perú
            </p>
          </div>
        </div>
        <div className="text-center text-gray-500 text-xs md:text-sm">
          © {new Date().getFullYear()} MonosStreaming. Todos los derechos reservados.
        </div>
      </footer>
    );
  };

  // Datos de plataformas
  const platforms = [
    {
      id: 1,
      name: 'Netflix',
      description: 'Miles de películas y series, incluyendo producciones originales galardonadas y éxitos globales.',
      price: 11.00,
      category: 'Video',
      image: 'https://logos-world.net/wp-content/uploads/2020/04/Netflix-Logo.png',
    },
    {
      id: 2,
      name: 'Max (HBO Max)',
      description: 'El hogar de HBO, Warner Bros., DC, Cartoon Network y más. Contenido premium y exclusivo.',
      price: 5.00,
      category: 'Video',
      image: 'https://logos-world.net/wp-content/uploads/2022/01/HBO-Max-Logo.png',
    },
    {
      id: 3,
      name: 'Disney+',
      description: 'Todo el contenido de Disney, Pixar, Marvel, Star Wars y National Geographic en un solo lugar.',
      price: 8.00,
      category: 'Video',
      image: 'https://logos-world.net/wp-content/uploads/2021/05/Disney-Logo-500x281.png',
    },
    {
      id: 4,
      name: 'Amazon Prime Video',
      description: 'Series y películas originales, además de una amplia biblioteca de contenido premium.',
      price: 7.00,
      category: 'Video',
      image: 'https://logos-world.net/wp-content/uploads/2021/02/Amazon-Prime-Video-Logo.png',
    },
    {
      id: 5,
      name: 'Crunchyroll',
      description: 'Plataforma de streaming especializada en anime, ofreciendo títulos exclusivos y estrenos simultáneos desde Japón.',
      price: 5.00,
      category: 'Video',
      image: 'https://logos-world.net/wp-content/uploads/2021/04/Crunchyroll-Logo-500x281.png',
    },
    {
      id: 6,
      name: 'YouTube',
      description: 'Plataforma de streaming de videos que ofrece contenido de todo tipo, incluyendo canales, música y contenido original.',
      price: 10.00,
      category: 'Video',
      image: 'https://logos-world.net/wp-content/uploads/2020/06/YouTube-Logo-500x281.png',
    },
    {
      id: 7,
      name: 'Spotify',
      description: 'Servicio de streaming musical que ofrece acceso a millones de canciones, listas de reproducción y contenido exclusivo.',
      price: 9.99,
      category: 'Audio',
      image: 'https://logos-world.net/wp-content/uploads/2020/10/Spotify-Logo-500x281.png',
    },
    {
      id: 8,
      name: 'Apple TV+',
      description: 'Plataforma de streaming que ofrece series, películas y documentales originales de Apple.',
      price: 6.00,
      category: 'Video',
      image: 'https://logos-world.net/wp-content/uploads/2021/03/Apple-TV-Logo-700x394.png',
    },
    {
      id: 9,
      name: 'VIX',
      description: 'Plataforma de streaming que ofrece una gran variedad de contenido en español, incluyendo películas, series y deportes.',
      price: 4.00,
      category: 'Video',
      image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6a/ViX_Logo.svg/2560px-ViX_Logo.svg.png',
    },
    {
      id: 10,
      name: 'Paramount+',
      description: 'Servicio de streaming con una amplia selección de series, películas y contenido exclusivo de Paramount y CBS.',
      price: 5.00,
      category: 'Video',
      image: 'https://logos-world.net/wp-content/uploads/2022/02/Paramount-Pictures-Logo-700x394.png',
    },
    {
      id: 11,
      name: 'Viki',
      description: 'Plataforma de streaming enfocada en dramas asiáticos, con subtítulos en varios idiomas y una gran comunidad de usuarios.',
      price: 6.00,
      category: 'Video',
      image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d9/Rakuten_Viki_Logo_2019.svg/1200px-Rakuten_Viki_Logo_2019.svg.png',
    },
    {
      id: 12,
      name: 'IPTV',
      description: 'Servicio de televisión por Internet que ofrece acceso a canales de televisión en vivo, películas y series a través de Internet.',
      price: 10.00,
      category: 'Video',
      image: 'https://www.svgrepo.com/show/517102/iptv-smarters.svg',
    },
  ];

  // Funciones de manejo
  const handleNavigate = (page) => {
    setCurrentPage(page);
  };

  const handleRegister = (name, email, password) => {
    setRegisteredUser({ name, email, password });
    alert('Registro exitoso. Ahora puedes iniciar sesión.');
    setCurrentPage('login');
  };

  const handleLogin = (name) => {
    setUser(name);
    alert(`¡Bienvenido, ${name}!`);
    setCurrentPage('home');
  };

  const handleLogout = () => {
    setUser(null);
    setRegisteredUser(null);
    alert('Has cerrado sesión');
    setCurrentPage('home');
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <StreamingHeader 
        onNavigate={handleNavigate} 
        user={user} 
        handleLogin={handleLogin} 
        handleLogout={handleLogout} 
      />
      <main className="flex-grow pt-24">
        {currentPage === 'home' && <StreamingHero onExplore={() => setCurrentPage('platforms')} />}
        {currentPage === 'platforms' && <StreamingPlatformGrid platforms={platforms} />}
        {currentPage === 'login' && <Login handleLogin={handleLogin} user={registeredUser} />}
        {currentPage === 'register' && <Registro handleRegister={handleRegister} />}
        {currentPage === 'about' && <AboutUs />}
        {currentPage === 'contact' && <Contact />}
      </main>
      <StreamingFooter />
        
        {/* Botón flotante WhatsApp */}
        <button
          onClick={() => {
            const phoneNumber = '51954380327';
            const message = '¡Hola! Estoy interesado en los servicios de MonosStreaming. ¿Podrías darme más información?';
            const whatsappUrl = `whatsapp://send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`;
            window.open(whatsappUrl, '_blank');
          }}
          className="fixed bottom-6 right-6 md:bottom-8 md:right-8 bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 transition-all duration-300 transform hover:scale-110 z-50 flex items-center justify-center text-lg font-bold"
          aria-label="Contactar por WhatsApp"
          title="Contactar por WhatsApp"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.106"/>
          </svg>
        </button>
      </div>
    );
  };


  export default App;